import { AdministrationPanels } from '@/models/enums/app/AdministrationPanels'
import { StoresList } from '@/models/enums/app/StoresList'
import type { User, UserAdminSubmission } from '@/models/interfaces/auth/User'
import { i18n } from '@/plugins/i18n'
import { UsersService } from '@/services/application/UsersService'
import { UserService } from '@/services/userAndAuth/UserService'
import { defineStore } from 'pinia'
import { reactive, ref, watch, type Reactive, type Ref } from 'vue'
import { useApplicationStore } from './applicationStore'
import { POSITION, useToast } from 'vue-toastification'

export const useAdminStore = defineStore(StoresList.ADMIN, () => {
  const selectedAdminPanel: Ref<AdministrationPanels> = ref(AdministrationPanels.MEMBERS)
  const selectedAdminItem: Ref<AdministrationPanels | null> = ref(
    AdministrationPanels.CONTENT_ACTORS,
  )

  const isSubmitting: Ref<boolean> = ref(false)
  const isError: Ref<boolean> = ref(false)
  const appMembers: Ref<User[]> = ref([])
  const toast = useToast()

  const getMembers = async () => {
    appMembers.value = await UsersService.getMembers(false)
  }

  async function createUser(user: UserAdminSubmission) {
    isSubmitting.value = true
    isError.value = false
    try {
      await UserService.createUser(user)
      await getMembers()
      userEdition.active = false

      toast.success(i18n.t('notifications.user.create'), {
        timeout: 3000,
        position: POSITION.TOP_CENTER,
      })
    } catch (error) {
      toast.error(i18n.t('notifications.user.createError'), {
        timeout: 3000,
        position: POSITION.TOP_CENTER,
      })
      console.error(error)
      isError.value = true
    } finally {
      isSubmitting.value = false
    }
  }

  const userEdition: Reactive<{ active: boolean; user: User | null }> = reactive({
    active: false,
    user: null,
  })

  watch(
    () => userEdition.active,
    () => {
      useApplicationStore().showEditContentDialog = userEdition.active
    },
  )

  function setUserEditionMode(user: User | null) {
    userEdition.user = user
    userEdition.active = true
    useApplicationStore().showEditContentDialog = true
  }

  async function editUser(values: UserAdminSubmission) {
    isSubmitting.value = true
    isError.value = false

    try {
      await UserService.patchUser(values, userEdition.user!.id)
      await getMembers()
      userEdition.active = false
      toast.success(i18n.t('notifications.user.edit'), {
        timeout: 3000,
        position: POSITION.TOP_CENTER,
      })
    } catch (error) {
      toast.error(i18n.t('notifications.user.createError'), {
        timeout: 3000,
        toastClassName: 'my-smooth-toast',
        position: POSITION.TOP_CENTER,
      })
      isError.value = true
      console.error(error)
    } finally {
      isSubmitting.value = false
    }
  }

  async function deleteUser(user: Partial<User>) {
    useApplicationStore().isLoading = true
    try {
      await UserService.deleteUser(user).then(() => {
        const index = appMembers.value.findIndex((member) => member.id === user.id)
        if (index !== -1) {
          appMembers.value.splice(index, 1)
          toast.success(i18n.t('notifications.user.deleteSuccess'))
        }
      })
    } catch (error) {
      console.error(error)
      toast.error(i18n.t('notifications.user.deleteError'))
    }
    useApplicationStore().isLoading = false
  }

  return {
    selectedAdminPanel,
    selectedAdminItem,
    appMembers,
    getMembers,
    createUser,
    deleteUser,
    userEdition,
    setUserEditionMode,
    editUser,
    isError,
    isSubmitting,
  }
})
